import { Trash2, Users } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import type {
  NativeSyntheticEvent,
  TextInputSubmitEditingEventData,
} from "react-native";
import { View, XStack, YStack } from "tamagui";
import { PrimaryCheck } from "../../../core/components/PrimaryCheck";
import { BodyTextarea } from "../../../ui/Body";
import { Checkbox } from "../../../ui/Checkbox";
import { InfoRow, type InfoRowItemProps } from "../../../ui/InfoRow";
import { PopoverConfirm } from "../../../ui/PopoverConfirm";
import type PollOptionModel from "./PollOptionModel";

const PollOptionUIInput: React.FC<{ data: PollOptionModel }> = observer(
  ({ data }) => {
    const saveOption = React.useCallback(
      (e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => {
        data.setOption(e.nativeEvent.text);
      },
      [data],
    );

    return (
      <BodyTextarea
        placeholder="Add option..."
        blurOnSubmit
        submitOnEnter
        flex={1}
        onSubmitEditing={saveOption}
        defaultValue={data.data.option}
      />
    );
  },
);

export const PollOptionUI = observer(({ data }: { data: PollOptionModel }) => {
  const infoRowItems = React.useMemo<InfoRowItemProps[]>(() => {
    const usersVoted = data.usersVotedStr;
    if (!usersVoted) {
      return [];
    }

    return [
      {
        icon: Users,
        text: usersVoted,
      },
    ];
  }, [data.usersVotedStr]);

  const isDone = data.module.isDone;

  let checkBoxArea = null;
  if (isDone) {
    checkBoxArea = (
      <View width={"$small"} marginTop={3}>
        {data.isInDoneResult && <PrimaryCheck />}
      </View>
    );
  } else {
    checkBoxArea = (
      <Checkbox
        checked={data.hasVotedForThis}
        onCheckedChange={() => {
          data.setAsVoted();
        }}
      />
    );
  }

  return (
    <XStack
      flex={1}
      width="100%"
      gap={"$space.small"}
      justifyContent="space-between"
    >
      <XStack
        flex={1}
        gap={"$space.medium"}
        justifyContent="flex-start"
        alignItems="flex-start"
      >
        {checkBoxArea}
        <YStack gap={"$space.medium"} flex={1}>
          <PollOptionUIInput data={data} />

          {infoRowItems.length > 0 && <InfoRow items={infoRowItems} />}
        </YStack>

        {data.module.isEditable && (
          <XStack justifyContent="flex-end" gap={"$space.small"}>
            <PopoverConfirm
              onConfirm={() => {
                data.deleteData(data.data.option);
              }}
            >
              <Trash2 size={"$size.normal"} color={"$muted"} />
            </PopoverConfirm>
          </XStack>
        )}
      </XStack>
    </XStack>
  );
});
